"use client";

import { Suspense } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { PanelIcon } from "@/components/icons";
import { useInboxCount } from "@/hooks/useInboxCount";
import { AutomationControls } from "./AutomationControls";
import { BusinessSwitcher } from "./BusinessSwitcher";
import { FolderTree } from "./FolderTree";
import { KnowledgeNav } from "./KnowledgeNav";
import { ModeSwitcher } from "./ModeSwitcher";
import { MonitoringNav } from "./MonitoringNav";
import { SettingsNav } from "./SettingsNav";
import { SidebarFooter } from "./SidebarFooter";
import { SidebarHeader } from "./SidebarHeader";
import type { SidebarMode } from "./types";
import { useFolderEditing } from "./useFolderEditing";
import { useFolderExpansion } from "./useFolderExpansion";
import { useKnowledgePages } from "./useKnowledgePages";
import { useSidebarData } from "./useSidebarData";

function sidebarModeFor(pathname: string): SidebarMode {
  return pathname === "/settings" || pathname.startsWith("/settings/")
    ? "settings"
    : pathname.startsWith("/knowledge")
    ? "kb"
    : pathname.startsWith("/monitoring")
      ? "reports"
      : "automation";
}

type SidebarProps = {
  /** Whether the rail is on screen. Its column stays mounted while collapsed, so the Inbox counts poll only while it is. */
  visible?: boolean;
  /** Collapse the desktop column. */
  onHide: () => void;
  /** Dismiss the mobile drawer. */
  onCloseDrawer: () => void;
};

function SidebarInner({ visible = true, onHide, onCloseDrawer }: SidebarProps) {
  const pathname = usePathname();
  const mode = sidebarModeFor(pathname);
  const data = useSidebarData(pathname);
  const expansion = useFolderExpansion();
  const editor = useFolderEditing({
    activeBusiness: data.activeBusiness,
    refreshAll: data.refreshAll,
    expandFolder: expansion.expand,
  });
  const kb = useKnowledgePages(mode === "kb", data.activeBusiness);
  // One business-wide poll for the whole sidebar; the Monitoring rail adds a department scope of its own.
  const inbox = useInboxCount(null, visible);

  // A department half-named under the old business is dropped with it.
  function switchBusiness(id: string) {
    editor.cancel();
    data.switchBusiness(id);
  }

  return (
    <>
      <SidebarHeader onHide={onHide} onCloseDrawer={onCloseDrawer} />

      <div className="shrink-0 space-y-1 px-3 pb-1 pt-1 md:pt-0">
        <BusinessSwitcher
          businesses={data.businesses}
          activeBusiness={data.activeBusiness}
          onSwitch={switchBusiness}
          refreshAll={data.refreshAll}
          refreshBusinesses={data.refreshBusinesses}
        />

        <ModeSwitcher mode={mode} pathname={pathname} inboxCount={inbox.count} onCloseDrawer={onCloseDrawer} />
        <Link href="/computers" onClick={onCloseDrawer} aria-current={pathname === "/computers" ? "page" : undefined}
          className={`flex items-center gap-2 rounded-md px-2 py-2 text-xs transition ${pathname === "/computers" ? "bg-zinc-800 text-violet-200" : "text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200"}`}>
          <PanelIcon className="size-4 shrink-0" />
          Computers
        </Link>
      </div>

      {mode === "settings" && <SettingsNav pathname={pathname} onCloseDrawer={onCloseDrawer} />}

      {mode === "automation" && (
        <FolderTree
          header={<AutomationControls activeBusiness={data.activeBusiness} onNewDepartment={() => editor.startCreate(null)} onCloseDrawer={onCloseDrawer} />}
          folders={data.scopedFolders}
          workers={data.scopedWorkers}
          pathname={pathname}
          onCloseDrawer={onCloseDrawer}
          expansion={expansion}
          editor={editor}
          onMoved={data.refreshAll}
        />
      )}

      {mode === "kb" && (
        <KnowledgeNav key={data.activeBusiness} businessId={data.activeBusiness} pages={kb.pages} rawSources={kb.rawSources}
          folders={kb.folders} pageFolders={kb.pageFolders} gapCount={kb.gapCount} onChanged={kb.refresh} onCloseDrawer={onCloseDrawer} />
      )}

      {mode === "reports" && (
        <MonitoringNav
          key={data.activeBusiness}
          activeBusiness={data.activeBusiness}
          folders={data.scopedFolders}
          workers={data.scopedWorkers}
          onChanged={data.refreshAll}
          pathname={pathname}
          businessInboxCount={inbox.count}
          visible={visible}
          onCloseDrawer={onCloseDrawer}
        />
      )}

      <SidebarFooter
        sessionInfo={data.sessionInfo}
        mode={mode}
        pathname={pathname}
        onCloseDrawer={onCloseDrawer}
      />
    </>
  );
}

export default function Sidebar(props: SidebarProps) {
  return (
    <Suspense fallback={null}>
      <SidebarInner {...props} />
    </Suspense>
  );
}
